import React, { useState, lazy, Suspense, useEffect } from 'react';
import PropTypes from 'prop-types';
import PrimaryButtonComponent from '../../Components/Buttons/PrimaryButton/PrimaryButton.component';
import { useNavigate } from 'react-router-dom';
import Auth from '../../Helpers/auth';
import { httpSendPurchaseEmailCompany } from '../../Hooks/orders/order.hooks';
import {
  httpCaptureOrderCompanyTransaction,
  httpCreateOrderCompanyTransaction,
} from '../../Hooks/orders/order.hooks';

const PayPalScriptProvider = lazy(() =>
  import('@paypal/react-paypal-js').then((module) => ({
    default: module.PayPalScriptProvider,
  }))
);
const PayPalButtons = lazy(() =>
  import('@paypal/react-paypal-js').then((module) => ({
    default: module.PayPalButtons,
  }))
);

// Send an email after the course has been purchased
// Show the transaction id
// Do the capture and create order in the backend for company
export default function PurchaseCompany({
  billingAddress,
  selectedUsers,
  discountCode,
  courseId,
  companyId,
  total,
  tax,
  grandTotal,
  discount,
  courseName,
}) {
  const [step, setStep] = useState(1);
  const [transactionId, setTransactionId] = useState(null);
  const [paypalInit, setPaypalInit] = useState(false);
  const { data: userData } = Auth.getProfile();
  const [emailSent, setEmailSent] = useState('');
  const navigate = useNavigate();
  const initialOptions = {
    'client-id': process.env.REACT_APP_PAY_PAL_CLIENT_ID,
    'enable-funding': 'venmo',
    currency: 'CAD',
    components: 'buttons',
  };

  useEffect(() => {
    setPaypalInit(true);
  }, []);

  async function createOrder() {
    try {
      if (!billingAddress || Object.keys(billingAddress).length === 0) {
        alert('Billing address is required.');
        throw new Error('Billing address is missing.');
      }
      if (!companyId) {
        alert('Company ID is required.');
        throw new Error('Company ID is missing.');
      }
      if (!courseId) {
        alert('Course ID is required.');
        throw new Error('Course ID is missing.');
      }
      if (!selectedUsers || selectedUsers.length === 0) {
        alert('At least one user must be selected.');
        throw new Error('No users selected.');
      }

      // Map selectedUsers to create users
      const users = selectedUsers.map(
        ({ _id, firstName, lastName, email }) => ({
          _id,
          firstName,
          lastName,
          email,
        })
      );

      const response = await httpCreateOrderCompanyTransaction(
        {
          cart: {
            courseId, // Include courseId
            users, // Pass mapped users as users
            discountCode, // Include discount code
            companyId,
          },
        },
        billingAddress, // Include billing address,
        { ...userData, companyId }
      );
      const orderData = await response.json();
      if (orderData.id) {
        return orderData.id;
      } else {
        alert('Error creating order. Please try again.');
        throw new Error('Could not create order');
      }
    } catch (error) {
      alert('Error creating order. Please try again.', error);
      console.error('Error creating order:', error);
      return null;
    }
  }

  async function onApprove(data) {
    // Map selectedUsers to create users
    const users = selectedUsers.map(({ _id, firstName, lastName, email }) => ({
      _id,
      firstName,
      lastName,
      email,
    }));
    try {
      const response = await httpCaptureOrderCompanyTransaction(
        data.orderID, // PayPal order ID
        courseId, // Include courseId
        users, // Pass mapped users as users
        billingAddress, // Include billing address
        discountCode, // Include discount code,
        { ...userData, companyId }
      );
      const orderData = await response.json();
      const transaction =
        orderData?.purchase_units?.[0]?.payments?.captures?.[0] ||
        orderData?.purchase_units?.[0]?.payments?.authorizations?.[0];
      if (!transaction || transaction.status === 'DECLINED') {
        throw new Error(`Transaction ${transaction.status}: ${transaction.id}`);
      } else {
        setEmailSent('Email Sending');
        let res = await httpSendPurchaseEmailCompany(
          transaction.id,
          courseId,
          users,
          billingAddress,
          discountCode,
          { ...userData, companyId },
          total,
          tax,
          grandTotal,
          discount
        );
        if (res.ok) {
          setEmailSent('');
        } else {
          setEmailSent(
            'Email has failed to send, please contact us if you would like your receipt'
          );
        }
      }
      setTransactionId(transaction.id);
      navigate('/company/transaction-completed', {
        state: {
          transactionId: transaction.id,
          total,
          tax,
          discountCode: discountCode || null,
          discountAmount: discount || null,
          grandTotal,
          users,
          courseName,
          billingAddress,
        },
      });
      setStep(2); // Move to the next step
    } catch (error) {
      console.error('Error capturing order:', error);
      alert('Transaction failed.', error);
    }
  }

  function onError(error) {
    console.error(error);
    alert('An error occurred during the payment process.');
  }

  if (step === 1) {
    return (
      <div className="paypal">
        <Suspense fallback={<div>Loading PayPal...</div>}>
          <PayPalScriptProvider options={initialOptions}>
            {paypalInit && (
              <PayPalButtons
                createOrder={createOrder}
                onApprove={onApprove}
                onError={onError}
                style={{
                  shape: 'pill',
                  layout: 'vertical',
                  color: 'gold',
                  label: 'pay',
                }}
              />
            )}
          </PayPalScriptProvider>
        </Suspense>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div
        style={{
          textAlign: 'center',
          paddingTop: '2rem',
          paddingBottom: '4rem',
          width: '75%',
          margin: '0 auto',
        }}
      >
        <h3>Purchase Completed</h3>
        <p>Thank you for your purchase!</p>
        <br />
        <p>An email has been sent with your transaction details</p>
        <br />
        <p>Your Order Id is: {transactionId}</p>
        <br />
        <p>{emailSent}</p>
        <PrimaryButtonComponent
          span="Go to Dashboard"
          functionName={() => navigate('/company')}
        />
      </div>
    );
  }

  return null;
}

PurchaseCompany.propTypes = {
  billingAddress: PropTypes.object,
  courseId: PropTypes.string,
  selectedUsers: PropTypes.array, // Renamed from users to selectedUsers
  discountCode: PropTypes.string,
  companyId: PropTypes.string,
  total: PropTypes.string,
  tax: PropTypes.string,
  grandTotal: PropTypes.string,
  discount: PropTypes.string,
  courseName: PropTypes.string,
};
